import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import useStateWithCallback from "use-state-with-callback";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  Link,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  AlertTitle,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import InfoIcon from "@mui/icons-material/InfoOutlined";

import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "@hookform/error-message";

import Form from "../forms";
// import Checkbox from "../forms/fields/checkbox";
import CurrencyInput from "../forms/fields/currencyInput";
import PDFView from "../../dialogs/pdfView";
import { numberToCurrency, sortArray } from "../../../libs/helpers";

import { useGA4React } from "ga-4-react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import FundFilterer from "../controls/fundFilterer";
import { LtePlusMobiledata } from "@mui/icons-material";
import PrimaryButton from "../buttons/primaryButton";
import CloseIcon from "@mui/icons-material/Close";
import Checkout from "../forms/fields/checkbox";
import { Report } from "../../../libs/loggers";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  radioTable: {
    marginTop: theme.spacing(3),
  },
  checkbox: {
    marginTop: theme.spacing(3),
  },
  input: {
    margin: theme.spacing(1, 1),
  },
}));

const Row = (props) => {
  const { fund } = props;
  const [selected, setSelected] = useState(false);
  const [openDO, setOpenDO] = useState(false);
  let page = window.location.pathname.replace(/^\/|\/$/g, "");
  //Get Vehicle
  const vehicle = useSelector((state) => state.data.value.vehicle);

  const classes = useStyles();
  let ga4React = useGA4React();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));

  const selectedFieldName = `${fund.id}-selected`;
  const lumpsumFieldName = `${fund.id}-lumpsum`;
  const monthlyFieldName = `${fund.id}-monthly`;
  const userId = useSelector((state) => state.submition.data?.userId);
  const submitionId = useSelector(
    (state) => state.submition.submition?.submitionId
  );
  const stepper = useSelector((state) => state.stepper.value);

  const validationSchema = yup.object().shape(
    {
      [lumpsumFieldName]: yup.number().when(monthlyFieldName, {
        is: (monthly) => !monthly || monthly === 0,
        then: yup
          .number()
          .required(
            fund.disableDebitOrder
              ? "Lump Sum is required"
              : "Either a Lump Sum or a Monthly Contribution is required"
          )
          .typeError("Lump Sum must be a number")
          .moreThan(
            props.minimums?.lumpsum - 1,
            `Lump Sum must be more than ${numberToCurrency(
              props.minimums?.lumpsum,
              0
            )} once off`
          ),
        otherwise: yup
          .number()
          .typeError("Lump Sum must be a number")
          .when(lumpsumFieldName, {
            is: (lumpsum) => lumpsum > 0,
            then: yup.number().typeError("Lump Sum must be a number"),
          }),
      }),
      [monthlyFieldName]: yup.number().when(lumpsumFieldName, {
        is: (lumpsum) => !lumpsum || lumpsum === 0,
        then: yup
          .number()
          .required("Either a Lump Sum or a Monthly Contribution is required")
          .typeError("Monthly Contribution must be a number")
          .moreThan(
            props.minimums?.monthly - 1,
            `Monthly Contribution must be more than ${numberToCurrency(
              props.minimums?.monthly,
              0
            )} p/m`
          )
          .when(monthlyFieldName, (monthlyFieldName, schema) => {
            return props.minimums?.monthlyMax !== null &&
              props.minimums?.monthlyMax !== undefined
              ? schema.lessThan(
                  props.minimums?.monthlyMax + 1,
                  `Monthly Contribution must be less than ${numberToCurrency(
                    props.minimums?.monthlyMax,
                    0
                  )} p/m`
                )
              : schema;
          }),

        otherwise: yup
          .number()
          .typeError("Monthly Contribution must be a number")
          .when(monthlyFieldName, {
            is: (monthly) => monthly !== 0,
            then: yup
              .number()
              .typeError("Monthly Contribution must be a number")
              .moreThan(
                props.minimums?.monthly - 1,
                `Monthly Contribution must be more than ${numberToCurrency(
                  props.minimums?.monthly,
                  0
                )} p/m`
              )
              .when(monthlyFieldName, (monthlyFieldName, schema) => {
                return props.minimums?.monthlyMax !== null &&
                  props.minimums?.monthlyMax !== undefined
                  ? schema.lessThan(
                      props.minimums?.monthlyMax + 1,
                      `Monthly Contribution must be less than ${numberToCurrency(
                        props.minimums?.monthlyMax,
                        0
                      )} p/m`
                    )
                  : schema;
              }),
          }),
      }),
    },
    [lumpsumFieldName, monthlyFieldName]
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    getValues,
    reset,
    unregister,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (props.value) {
      setValue(lumpsumFieldName, props.value.lumpsum);
      setValue(monthlyFieldName, props.value.monthly);

      setSelected(true);
    } else {
      reset();
    }

    return () => {
      setSelected(false);
    };
  }, [props.filter, props.value]);

  const onFundSelect = (e) => {
    if (!e.target.checked) {
      setSelected(false);
      handleSubmit((data) => onSubmit(data, false))();
    }

    const eventInfo = {
      action: "select",
      category: "investment-solution",
      description: "select investment solution",
      value: {
        currentPage: page,
        step: stepper.active + 1,
        value: "select_investment_solution",
        fund: e.target.value,
      },
    };

    Report(eventInfo, userId, submitionId);

    reset();
    setSelected(e.target.checked);
  };

  const onChange = async () => {
    if (await trigger([lumpsumFieldName, monthlyFieldName])) {
      handleSubmit((data) => onSubmit(data, true))();
    }
  };

  const viewFundFactSheet = (props) => {
    const eventInfo = {
      action: "view",
      category: "document",
      description: "view fund document",
      value: {
        currentPage: page,
        step: stepper.active + 1,
        value: props,
      },
    };
    Report(eventInfo, userId, submitionId);
  };
  const onBlur = async () => {
    if (await trigger([lumpsumFieldName, monthlyFieldName])) {
      ga4React.event("investment_select", fund.name, "investment_solution");

      //   if (cbSelectedWatch) {
      //     if (getValues(lumpsumFieldName) && getValues(lumpsumFieldName) !== 0)
      //       ga4React.event(
      //         "amount_update_lumpsum",
      //         fund.name,
      //         "investment_solution"
      //       );

      //     if (getValues(monthlyFieldName) && getValues(monthlyFieldName) !== 0)
      //       ga4React.event(
      //         "amount_update_monthly",
      //         fund.name,
      //         "investment_solution"
      //       );
      //   }
    }
  };

  const onSubmit = (data, s) => {
    const submitData = {
      lumpsum: data[lumpsumFieldName],
      monthly: data[monthlyFieldName],
      selected: s,
    };

    props.onChange(submitData);
  };

  return (
    <Fragment>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        role="checkbox"
        aria-checked={selected}
        tabIndex={-1}
        selected={selected}
      >
        <TableCell padding="normal">
          <Checkbox
            name={selectedFieldName}
            color="primary"
            label=""
            onChange={(e) => onFundSelect(e)}
            checked={selected}
            value={fund.name}
          />
        </TableCell>
        <TableCell>
          <Typography>{fund.name}</Typography>
        </TableCell>
        <TableCell padding={"normal"}>
          {fund.factSheet && !mobile ? (
            <PDFView
              file={fund.factSheet}
              title="Factsheet"
              buttonType={"Icon"}
              icon={InfoIcon}
            />
          ) : fund.factSheet && mobile ? (
            <Link
              onClick={() => viewFundFactSheet(fund.factSheet)}
              href={fund.factSheet}
              target="_blank"
            >
              <InfoIcon color="primary" />
            </Link>
          ) : null}
        </TableCell>

        {mobile && (
          <TableCell>
            <Typography>{fund.risk}</Typography>
          </TableCell>
        )}
        {mobile ? (
          <>
            {props.filter.category === "time" && (
              <TableCell>
                <Typography>{fund.time}</Typography>
              </TableCell>
            )}

            {props.filter.category === "assetHoldings" && (
              <TableCell>
                <Typography>{fund.assetHoldings}</Typography>
              </TableCell>
            )}

            {props.filter.category === "assetAllocation" && (
              <TableCell>
                <Typography>{fund.regionalHoldings}</Typography>
              </TableCell>
            )}
          </>
        ) : (
          <>
            <TableCell>
              <Typography>{fund.time}</Typography>
            </TableCell>
            <TableCell>
              <Typography>{fund.risk}</Typography>
            </TableCell>
            <TableCell>
              <Typography>{fund.assetHoldings}</Typography>
            </TableCell>
            <TableCell>
              <Typography>{fund.regionalHoldings}</Typography>
            </TableCell>
          </>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={selected} timeout="auto" unmountOnExit>
            <Box
              sx={{ margin: 1, display: "flex" }}
              justifyContent={"space-evenly"}
            >
              <CurrencyInput
                type="number"
                id={lumpsumFieldName}
                label="Lump Sum"
                symbol="R"
                error={!!errors[lumpsumFieldName]}
                helperText={errors?.[lumpsumFieldName]?.message}
                control={control}
                name={lumpsumFieldName}
                className={classes.input}
                onChange={onChange}
                onBlur={onBlur}
                disabled={props.value?.isHelp}
              />
              {!fund.disableDebitOrder ? (
                <CurrencyInput
                  control={control}
                  name={monthlyFieldName}
                  className={classes.input}
                  type="number"
                  id={monthlyFieldName}
                  label="Monthly Contribution"
                  symbol="R"
                  error={!props.value?.isHelp && !!errors[monthlyFieldName]}
                  helperText={
                    !props.value?.isHelp && errors?.[monthlyFieldName]?.message
                  }
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={props.value?.isHelp}
                />
              ) : mobile ? (
                fund.deitOrderNote && (
                  <>
                    <IconButton onClick={() => setOpenDO(true)}>
                      <InfoIcon
                        color="primary"
                        sx={{ mr: "8px", fontSize: "1rem" }}
                      />
                      <Typography color="primary" variant={"h6"}>
                        Debit Order
                      </Typography>
                    </IconButton>
                    <Dialog open={openDO} onClose={() => setOpenDO(false)}>
                      <DialogTitle>
                        Debit Order
                        <IconButton
                          aria-label="close"
                          onClick={() => setOpenDO(false)}
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </DialogTitle>
                      <DialogContent>
                        <DialogContentText>
                          {fund.deitOrderNote}
                          <Link
                            href="mailto:clientservices@fairtreeinvest.com"
                            sx={{ cursor: "pointer" }}
                          >
                            clientservices@fairtreeinvest.com
                          </Link>
                        </DialogContentText>
                      </DialogContent>
                      <DialogActions>
                        <PrimaryButton
                          variant={"outlined"}
                          onClick={() => setOpenDO(false)}
                        >
                          Close
                        </PrimaryButton>
                      </DialogActions>
                    </Dialog>
                  </>
                )
              ) : (
                fund.deitOrderNote && (
                  <Typography sx={{ ml: 3, mr: 3 }}>
                    {fund.deitOrderNote}{" "}
                    <Link
                      href="mailto:clientservices@fairtreeinvest.com"
                      sx={{ cursor: "pointer" }}
                    >
                      clientservices@fairtreeinvest.com
                    </Link>
                  </Typography>
                )
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

function FundSelector({ onFundsUpdate, savedFunds }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedFunds, setSelectedFunds] = useState(
    savedFunds ? savedFunds : []
  );

  const funds = useSelector((state) =>
    state.investVehicle.value.funds.filter(
      (f) => f.vehicles.indexOf(state.data.value.vehicle) !== -1
    )
  );

  const submitionId = useSelector(
    (state) => state.submition.submition?.submitionId
  );
  const userId = useSelector((state) => state.submition.data?.userId);
  const [filteredFunds, setFilteredFunds] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState({});
  let page = window.location.pathname.replace(/^\/|\/$/g, "");
  const vehicle = useSelector((state) =>
    state.investVehicle.value.vehicles.find(
      (v) => v.name === state.data.value.vehicle
    )
  );
  //   const savedFunds = useSelector((state) => state.data.value.funds);

  const schema = yup.object().shape({
    funds: yup
      .array()
      .min(1, "Please select at least one fund.")
      .required("Please select at least one fund."),
  });

  const { register, setValue } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    register("funds");
  }, [register]);

  useEffect(() => {
    setValue("funds", selectedFunds, { shouldValidate: true });
  }, [selectedFunds, setValue]);

  const handleChange = (data, fund) => {
    let currentFunds = selectedFunds.map((obj) => Object.assign({}, obj));

    if (!data.selected) {
      const fundIndex = currentFunds.findIndex((f) => f.fund.id === fund.id);

      if (fundIndex !== -1) {
        currentFunds.splice(fundIndex, 1);
      }
    }

    if (data.selected && currentFunds) {
      const fundIndex = currentFunds.findIndex((f) => f.fund.id === fund.id);

      if (fundIndex !== -1) {
        currentFunds[fundIndex].lumpsum = data.lumpsum;
        currentFunds[fundIndex].monthly = data.monthly;
      } else {
        currentFunds.push({
          fund,
          lumpsum: data.lumpsum,
          monthly: data.monthly,
        });
      }
    }

    setSelectedFunds(currentFunds);
    onFundsUpdate(currentFunds);
  };

  return (
    <Form>
      <Typography mb={1} variant={"body1"} mt={3}>
        You may select more than one fund and allocate to your investment
        accordingly.
      </Typography>

      {/* <FundFilterer onFilterChange={(filter) => handleFilter(filter)} /> */}
      <Table size="small" padding={mobile ? "none" : "normal"}>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>
              <Typography variant="tableHeading">Fund</Typography>
            </TableCell>
            <TableCell></TableCell>
            {mobile && (
              <TableCell>
                <Typography variant="tableHeading">Risk</Typography>
              </TableCell>
            )}

            {mobile ? (
              <>
                {selectedFilter.category === "time" && (
                  <TableCell>
                    <Typography variant="tableHeading">Time</Typography>
                  </TableCell>
                )}

                {selectedFilter.category === "assetHoldings" && (
                  <TableCell>
                    <Typography variant="tableHeading">
                      Asset Holdings
                    </Typography>
                  </TableCell>
                )}

                {selectedFilter.category === "assetAllocation" && (
                  <TableCell>
                    <Typography variant="tableHeading">
                      Regional Holdings
                    </Typography>
                  </TableCell>
                )}
              </>
            ) : (
              <>
                <TableCell>
                  <Typography variant="tableHeading">Time</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="tableHeading">Risk</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="tableHeading">Asset Holdings</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="tableHeading">
                    Regional Holdings
                  </Typography>
                </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortArray(funds).map((fund, index) => (
            <Row
              key={index}
              fund={fund}
              onChange={(data) => handleChange(data, fund)}
              minimums={vehicle?.minimums || null}
              filter={selectedFilter}
              selectedFunds={selectedFunds}
              value={
                selectedFunds && fund
                  ? selectedFunds.find((f) => f.fund?.id === fund.id)
                  : null
              }
            />
          ))}

          {funds.length === 0 && (
            <TableRow>
              <TableCell colSpan={mobile ? 4 : 7}>
                <Typography variant="h6" textAlign="center">
                  No funds available in this category, please keep browsing
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Form>
  );
}

export default FundSelector;
